/**
 * Every write of the pieces a reader edits goes out from here, one at a time
 * and in the order it was asked for, so two writes of one piece never race to
 * the host.
 */

// A draft saves as it is written, and the host takes at most fifty writes of
// a method in thirty seconds: a draft's save waits for a second without changes.
const AUTOSAVE_DELAY_MS = 1000;

/** Tells a form whether a write the operator asked for is still waiting for the host. */
export type BusyFlag = (busy: boolean) => void;

type Write<T> = () => Promise<T>;

/** A write that reads the form only when it goes. */
interface DraftWrite {
    form: object;
    /** The save that closes the form once it answers. */
    closes: boolean;
    started: boolean;
    again: boolean;
}

export class AddOnWriteQueue {
    private tail: Promise<unknown> = Promise.resolve();
    private readonly waiting = new Map<object, { writes: number; flag: BusyFlag }>();
    private readonly drafts = new Set<DraftWrite>();
    private timer: ReturnType<typeof setTimeout> | undefined;

    /** Queues a write the operator asked for, and holds the form busy until the last of them settles. */
    write<T>(form: object, run: Write<T>, flag: BusyFlag): Promise<T> {
        this.hold(form, flag);
        return this.enqueue(run).finally(() => this.release(form));
    }

    /** Queues a write no form owns, which holds nothing busy and only waits its turn. */
    enqueue<T>(run: Write<T>): Promise<T> {
        const answer = this.tail.then(run);
        // A refused write never holds up the ones behind it.
        this.tail = answer.catch(() => undefined);

        return answer;
    }

    /**
     * Queues the save that closes a draft's form, which holds the form busy
     * like any write the operator asks for. It reads the form only when it
     * goes, so a change made while it waits joins it, and one made while it
     * travels sends it again once it answers.
     */
    writeDraft<T>(form: object, run: Write<T>, flag: BusyFlag): Promise<T> {
        this.hold(form, flag);
        return this.enqueueDraft(form, run, { closes: true }).finally(() => this.release(form));
    }

    /**
     * A change to a draft: its save goes out a second later, and a newer
     * change moves it. It holds nothing busy: a write the operator asks for
     * meanwhile only waits its turn behind it. A change made while the save
     * closing the form travels goes with that save at once, since the form
     * closes as soon as it answers.
     */
    autosave(form: object, run: Write<unknown>): void {
        this.dropAutosave();

        const travelling = this.travellingDraftOf(form);
        if (travelling?.closes) {
            travelling.again = true;
            return;
        }

        this.timer = setTimeout(() => {
            this.timer = undefined;
            this.requestSave(form, run);
        }, AUTOSAVE_DELAY_MS);
    }

    /** Drops the change still waiting for its second, which the write asked for now sends anyway. */
    dropAutosave(): void {
        clearTimeout(this.timer);
        this.timer = undefined;
    }

    /** Is a write the operator asked for on this form still waiting for the host? */
    busy(form: object): boolean {
        return this.waiting.has(form);
    }

    /**
     * A change joins a draft write of its form that has yet to go, which
     * reads the draft only then, or sends the one travelling again once it
     * answers, so only the latest draft goes out and nothing typed is lost.
     */
    private requestSave(form: object, run: Write<unknown>): void {
        if (this.draftsOf(form).some(draft => !draft.started)) return;

        const travelling = this.travellingDraftOf(form);
        if (travelling) {
            travelling.again = true;
            return;
        }

        this.enqueueDraft(form, run, { closes: false });
    }

    private draftsOf(form: object): DraftWrite[] {
        return [...this.drafts].filter(draft => draft.form === form);
    }

    /** The draft write of this form waiting for the host, if any: one goes at a time. */
    private travellingDraftOf(form: object): DraftWrite | undefined {
        return this.draftsOf(form).find(draft => draft.started);
    }

    private enqueueDraft<T>(form: object, run: Write<T>, { closes }: { closes: boolean }): Promise<T> {
        const draft: DraftWrite = { form, closes, started: false, again: false };
        this.drafts.add(draft);

        return this.enqueue(async () => {
            draft.started = true;
            try {
                let answer: T;
                do {
                    draft.again = false;
                    answer = await run();
                } while (draft.again);

                return answer;
            } finally {
                this.drafts.delete(draft);
            }
        });
    }

    private hold(form: object, flag: BusyFlag): void {
        const entry = this.waiting.get(form);
        if (entry) {
            entry.writes += 1;
            return;
        }

        this.waiting.set(form, { writes: 1, flag });
        flag(true);
    }

    private release(form: object): void {
        const entry = this.waiting.get(form);
        if (!entry) return;

        entry.writes -= 1;
        if (entry.writes > 0) return;

        this.waiting.delete(form);
        entry.flag(false);
    }
}

// Per store, so a timer one store set never writes through another.
const queues = new WeakMap<object, AddOnWriteQueue>();

/** The write queue of the store that holds `state`. */
export function addOnWriteQueueOf(state: object): AddOnWriteQueue {
    let queue = queues.get(state);
    if (!queue) {
        queue = new AddOnWriteQueue();
        queues.set(state, queue);
    }

    return queue;
}
